import { TabPanel } from '@/common/utils/types';
import { EventListItem } from '@/alert/utils/types';
import { i18n } from '@/common/locale';
import { DEFAULT_PERIOD_ITEMS } from '@/common/components/molecules/timeController/constants';
import { SelectOption } from '@/types/common.types';
import { RuleCriteria } from '@/alert/components/alertDetail/alertDetail.types';
import { CheckInterval, EventType } from '@/config/views/userAlert/alertRuleWindow.types';
import { ViewMode } from '@/common/stores/view-mode';
import { DB_TYPE, VIEW_MODE } from '@/common/define/common.define';
import { startCase } from 'lodash-es';

export const ALERT_LEVEL = {
  NORMAL: 'Normal',
  CRITICAL: 'Critical',
  WARNING: 'Warning',
} as const;

export const ALERT_TYPE_VALUE = {
  SYSTEM: 'System',
  METRIC: 'Metric',
  EVENT: 'Event',
  PROCESS: 'Process',
  TABLE: 'Table',
  PROMETHEUS: 'Prometheus',
  ELASTICSEARCH: 'Elasticsearch',
  EVENT_STATISTICS: 'EventStatistics',
} as const;
export const getAlertTypeItems = ({
  containExternal = false,
}: {
  containExternal?: boolean;
} = {}): SelectOption<string>[] => {
  const baseItems = [
    { name: i18n.global.t('WORD.METRIC'), value: ALERT_TYPE_VALUE.METRIC },
    { name: i18n.global.t('WORD.EVENT'), value: ALERT_TYPE_VALUE.EVENT },
    { name: i18n.global.t('WORD.TABLE'), value: ALERT_TYPE_VALUE.TABLE },
    { name: i18n.global.t('WORD.EVENT_STATISTICS'), value: ALERT_TYPE_VALUE.EVENT_STATISTICS },
    { name: i18n.global.t('WORD.SYSTEM'), value: ALERT_TYPE_VALUE.SYSTEM },
  ];
  if (containExternal) {
    return [
      ...baseItems,
      { name: 'Prometheus', value: ALERT_TYPE_VALUE.PROMETHEUS },
      { name: 'Elasticsearch', value: ALERT_TYPE_VALUE.ELASTICSEARCH },
    ];
  }
  return baseItems;
};

export const CHECK_BY_TARGET = 'Check by Target';
export const CHECK_BY_COMBINATION = 'Check by Combination';
export const RULE_CRITERIA_ITEMS: SelectOption<RuleCriteria>[] = [
  { name: CHECK_BY_TARGET, value: CHECK_BY_TARGET },
  { name: CHECK_BY_COMBINATION, value: CHECK_BY_COMBINATION },
];

export const ALERT_CONDITION_TYPE = {
  SINGLE: 'Single Alert',
  MULTI: 'Multi Alert',
  CUSTOM: 'Custom',
} as const;
export type AlertConditionTypeValue =
  (typeof ALERT_CONDITION_TYPE)[keyof typeof ALERT_CONDITION_TYPE];
export const getCheckIntervalItems = (): SelectOption<CheckInterval['unit']>[] => [
  { name: i18n.global.t('DESC.UI.SECOND'), value: 's' },
  { name: i18n.global.t('DESC.UI.MINUTE'), value: 'm' },
  { name: i18n.global.t('DESC.UI.HOUR'), value: 'h' },
  { name: i18n.global.t('DESC.UI.DAY'), value: 'd' },
];
export const getCheckIntervalSecondsItems = (): SelectOption<number>[] =>
  new Array(12).fill(null).map((v, idx) => {
    const sec = (idx + 1) * 5;
    return sec === 60
      ? { name: i18n.global.t('WORD.MINUTES', { value: sec / 60 }), value: sec }
      : { name: i18n.global.t('WORD.SECONDS', { value: sec }), value: sec };
  });

export const getCheckHoldTimeItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.SECONDS', { value: 0 }), value: 'p0s' },
  { name: i18n.global.t('WORD.SECONDS', { value: 30 }), value: 'p30s' },
  { name: i18n.global.t('WORD.MINUTE', { value: 1 }), value: 'p1m' },
  { name: i18n.global.t('WORD.MINUTES', { value: 10 }), value: 'p10m' },
  { name: i18n.global.t('WORD.MINUTES', { value: 30 }), value: 'p30m' },
  { name: i18n.global.t('WORD.HOUR', { value: 1 }), value: 'p1h' },
  { name: i18n.global.t('WORD.HOURS', { value: 6 }), value: 'p6h' },
  { name: i18n.global.t('WORD.HOURS', { value: 12 }), value: 'p12h' },
  { name: i18n.global.t('WORD.DAY', { value: 1 }), value: 'p1d' },
];
export const getWeeklyScheduleItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.MONDAY'), value: 'Mon' },
  { name: i18n.global.t('WORD.TUESDAY'), value: 'Tue' },
  { name: i18n.global.t('WORD.WEDNESDAY'), value: 'Wed' },
  { name: i18n.global.t('WORD.THURSDAY'), value: 'Thu' },
  { name: i18n.global.t('WORD.FRIDAY'), value: 'Fri' },
  { name: i18n.global.t('WORD.SATURDAY'), value: 'Sat' },
  { name: i18n.global.t('WORD.SUNDAY'), value: 'Sun' },
];
export const getAggregationItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.AVG'), value: 'avg' },
  { name: i18n.global.t('WORD.MAX'), value: 'max' },
  { name: i18n.global.t('WORD.MIN'), value: 'min' },
  { name: i18n.global.t('WORD.SUM'), value: 'sum' },
];
export const COMPARISON_ITEMS: SelectOption<string>[] = [
  { name: '>', value: '>' },
  { name: '>=', value: '>=' },
  { name: '<', value: '<' },
  { name: '<=', value: '<=' },
  { name: '==', value: '==' },
];

export const STRING_COMPARISON_ITEMS: SelectOption<string>[] = [
  { name: 'like', value: 'like' },
  { name: 'not like', value: 'not like' },
  { name: '==', value: '==' },
  { name: '!=', value: '!=' },
];

export const getEvaluateValueItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.AVG'), value: 'avg' },
  { name: i18n.global.t('WORD.SUM'), value: 'sum' },
  { name: i18n.global.t('WORD.CURRENT'), value: 'current' },
  { name: i18n.global.t('WORD.CHANGE'), value: 'change' },
  {
    name: `${i18n.global.t('WORD.SUM')}(${i18n.global.t('WORD.PER_SEC')})`,
    value: 'sumPerSec',
  },
  {
    name: `${i18n.global.t('WORD.CHANGE')}(${i18n.global.t('WORD.PER_SEC')})`,
    value: 'changePerSec',
  },
];

export const EVALUATE_VALUE_TO_SUMMARY_TYPE: Record<string, string> = {
  avg: 'avg',
  max: 'max',
  min: 'min',
  current: 'current',
  change: 'delta',
  sum: 'sum',
  changePerSec: 'deltaPerSec',
  sumPerSec: 'sumPerSec',
};

const timeUnitStr = {
  m: 'WORD.MINUTES',
  h: 'WORD.HOURS',
  d: 'WORD.DAY',
};
export const getDuringTimeItems = (): SelectOption<string>[] => {
  return DEFAULT_PERIOD_ITEMS.map((item) => {
    const numberStr = item.value.replace(/\D/g, '');
    const timeUnit = item.value.slice(-1);
    const name = `${i18n.global.t('WORD.LAST')} ${i18n.global.t(timeUnitStr[timeUnit], {
      value: parseInt(numberStr, 10),
    })}`;
    return {
      name,
      value: item.value,
    };
  });
};

export const { ENUM: EVENT_TYPE_ENUM, REQ_ENUM: EVENT_TYPE_REQ_ENUM } = (() => {
  const ENUM = {
    HOST: 'Host',
    NETWORK: 'Network',
    DATABASE: 'Database',
    KUBERNETES: 'Kubernetes',
    KUBERNETES_PHASE: 'Kubernetes Phase',
    APPLICATION: 'Application',
    LOGGING: 'Logging',
  } as const;
  const REQ_ENUM = Object.keys(ENUM).reduce(
    (acc, key) => {
      acc[ENUM[key]] = ['APPLICATION', 'HOST'].includes(key) ? ENUM[key] : key;
      return acc;
    },
    {} as Record<(typeof ENUM)[keyof typeof ENUM], keyof typeof ENUM>,
  );

  return {
    ENUM,
    REQ_ENUM,
  };
})();

export const ALERT_TARGET_CATEGORIES = {
  [ALERT_TYPE_VALUE.METRIC]: {
    [ALERT_TYPE_VALUE.PROCESS]: ['host'],
  },
  [ALERT_TYPE_VALUE.EVENT]: {
    [EVENT_TYPE_ENUM.HOST]: ['host'],
    [EVENT_TYPE_ENUM.NETWORK]: ['network_device'],
    [EVENT_TYPE_ENUM.APPLICATION]: ['application'],
  },
};

export const EVENT_NAME_ENUM = {
  PROCESS_DOWN_DETECTION: 'Process Down Detection',
  PROCESS_COUNT_DETECTION: 'Process Count Detection',
  NETWORK_SYSLOG: 'Network Syslog',
  NETWORK_TRAP: 'Network Trap',
  FILE: 'File',
  SERVER_CHANGE: 'Server Change Event',
  KUBERNETES_REASON: 'Reason',
  KUBERNETES_PHASE_STATUS: 'Phase Status',
  DATABASE_SLOW_QUERY: 'Slow Query',
};

export const getEventTypeItems = (
  viewMode: ViewMode = VIEW_MODE.EXEMONE,
): SelectOption<EventType>[] => {
  const host = { name: i18n.global.t('WORD.HOST'), value: EVENT_TYPE_ENUM.HOST };
  const network = { name: i18n.global.t('WORD.NETWORK'), value: EVENT_TYPE_ENUM.NETWORK };
  const database = { name: i18n.global.t('WORD.DATABASE'), value: EVENT_TYPE_ENUM.DATABASE };
  const kubernetes = { name: i18n.global.t('WORD.KUBERNETES'), value: EVENT_TYPE_ENUM.KUBERNETES };
  const application = {
    name: i18n.global.t('WORD.APPLICATION'),
    value: EVENT_TYPE_ENUM.APPLICATION,
  };
  const logging = { name: i18n.global.t('WORD.LOGGING'), value: EVENT_TYPE_ENUM.LOGGING };

  const itemsByViewMode = {
    [VIEW_MODE.EXEMONE]: [host, network, database, kubernetes, application, logging],
    [VIEW_MODE.MAXGAUGE]: [host, database, logging],
    [VIEW_MODE.INTERMAX]: [host, network, application, logging],
    [VIEW_MODE.CLOUDMOA]: [host, network, kubernetes, application, logging],
  };
  return itemsByViewMode[viewMode] || itemsByViewMode[VIEW_MODE.EXEMONE];
};

const HOST_EVENT_ITEMS = (): SelectOption<string>[] => [
  {
    name: i18n.global.t('WORD.PROCESS_DOWN_DETECTION'),
    value: EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION,
  },
  {
    name: i18n.global.t('WORD.PROCESS_COUNT_CHANGE_DETECTION'),
    value: EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION,
  },
  // { name: i18n.global.t('WORD.FILE'), value: EVENT_NAME_ENUM.FILE },
  // { name: i18n.global.t('WORD.SERVER_CHANGE_EVENT'), value: EVENT_NAME_ENUM.SERVER_CHANGE },
];

const NETWORK_EVENT_ITEMS = (): SelectOption<string>[] => [
  { name: 'Syslog', value: EVENT_NAME_ENUM.NETWORK_SYSLOG },
  { name: 'Trap', value: EVENT_NAME_ENUM.NETWORK_TRAP },
];

const DATABASE_EVENT_ITEMS = (): SelectOption<string>[] => [
  { name: EVENT_NAME_ENUM.DATABASE_SLOW_QUERY, value: EVENT_NAME_ENUM.DATABASE_SLOW_QUERY },
];

export const DATABASE_SLOW_QUERY_THRESHOLD_DEFAULT_MS = 3000;

const APP_EVENT_ITEMS = (): SelectOption<string>[] => [
  { name: 'XM_OUTOFMEMORYERROR', value: 'XM_OUTOFMEMORYERROR' },
  { name: 'XM_SOCKETTIMEOUTEXCEPTION', value: 'XM_SOCKETTIMEOUTEXCEPTION' },
  { name: 'XM_SOCKETEXCEPTION', value: 'XM_SOCKETEXCEPTION' },
  { name: 'XM_TXN_PLC', value: 'XM_TXN_PLC' },
  { name: 'XM_JDBC_CONNECTION_FAIL', value: 'XM_JDBC_CONNECTION_FAIL' },
  { name: 'XM_JDBC_TOOMANYFETCH', value: 'XM_JDBC_TOOMANYFETCH' },
];

export const getEventNameItems = (type: EventType): SelectOption<string>[] => {
  const EVENT_NAME_ITEMS: Record<
    | typeof EVENT_TYPE_ENUM.HOST
    | typeof EVENT_TYPE_ENUM.NETWORK
    | typeof EVENT_TYPE_ENUM.DATABASE
    | typeof EVENT_TYPE_ENUM.APPLICATION,
    SelectOption<string>[]
  > = {
    [EVENT_TYPE_ENUM.HOST]: HOST_EVENT_ITEMS(),
    [EVENT_TYPE_ENUM.NETWORK]: NETWORK_EVENT_ITEMS(),
    [EVENT_TYPE_ENUM.DATABASE]: DATABASE_EVENT_ITEMS(),
    [EVENT_TYPE_ENUM.APPLICATION]: APP_EVENT_ITEMS(),
  };
  return EVENT_NAME_ITEMS[type] ?? [];
};

export const getFileTypeItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.FILE_SIZE'), value: 'fileSize' },
  { name: i18n.global.t('WORD.FILE_CHANGE'), value: 'fileChange' },
  { name: i18n.global.t('WORD.FILE_NO_CHANGE'), value: 'fileNoChange' },
];

export const FILE_SIZE_UNIT_ITEMS = [
  { name: 'Byte', value: 'Byte' },
  { name: 'KiB', value: 'KiB' },
  { name: 'MiB', value: 'MiB' },
  { name: 'GiB', value: 'GiB' },
];

export const getFileNoChangeUnitItems = (): SelectOption<string>[] => [
  { name: i18n.global.t('WORD.MINUTE'), value: 'm' },
  { name: i18n.global.t('WORD.HOUR'), value: 'h' },
  { name: i18n.global.t('WORD.DAY'), value: 'd' },
];

export const HASH_FUNCTION_ITEMS = [
  { name: 'CRC32', value: 'CRC32' },
  { name: 'MD5', value: 'MD5' },
  { name: 'SHA256', value: 'SHA256' },
];

export const FIXED_CONTENT = {
  [CHECK_BY_TARGET]: `
$alert$trigger_time$
$alert$rule_name$
$alert$targets$
$alert$alert_name$ : $alert$level$ ($alert$value$)`,
  [CHECK_BY_COMBINATION]: `
$alert$trigger_time$
$alert$rule_name$ : $alert$level$
$alert$targets$
$alert$alert_name$ : $alert$level$ ($alert$value$),
$alert$targets$
$alert$alert_name$ : $alert$level$ ($alert$value$)`,
} as const;

export const CONSOLIDATED_FIXED_CONTENT = `
$alert_summary$time_window$
$alert_summary$rule_name$
$alert_summary$count_bylevel$
$alert_summary$targets$
$alert_summary$value$`;

export const getConsolidatedFixedContentSample = () =>
  i18n.global.t('MESSAGE.CONSOLIDATED_FIXED_CONTENT_SAMPLE');

export const getNotificationFixedContentSample = (ruleCriteria: string) =>
  ruleCriteria === CHECK_BY_TARGET
    ? i18n.global.t('MESSAGE.NOTIFICATION_FIXED_CONTENT_SAMPLE_CHECK_BY_TARGET')
    : i18n.global.t('MESSAGE.NOTIFICATION_FIXED_CONTENT_SAMPLE_CHECK_BY_COMBINATION');

export const NOTIFICATION_FIXED_CONTENT_SAMPLE_KAKAOTALK = {
  ko: `[엑셈원] 모니터링 알람 발생 안내

안녕하세요 #{alert_user_name}님,
고객님께서 알림 설정하신 지표 및 이벤트에 대한 모니터링 알람이 발생하여 안내드립니다.

▶ 제목 : #{alert_title}
▶ 메시지 : #{alert_message}
▶ 발생시각 : #{alert_trigger_time}
▶ 룰이름 : #{alert_rule_name}
▶ 발생대상 : #{alert_targets}
▶ 알림내용 : #{alert_alert_name} : #{alert_level} (#{alert_value})

※ 이 메시지는 고객님께서 요청하신 알림 설정을 기준으로 발송되었습니다.`,
  en: `[exemONE] Monitoring Alert Notification

Hello #{alert_user_name},
This is a notification regarding the monitoring alert triggered for the metrics and events you have set up.

▶ Title : #{alert_title}
▶ Message : #{alert_message}
▶ Triggered Time : #{alert_trigger_time}
▶ Rule Name : #{alert_rule_name}
▶ Target : #{alert_targets}
▶ Alert Details : #{alert_alert_name} : #{alert_level} (#{alert_value})

※ This message was sent based on your notification setting.`,
};

export const SYSLOG_FACILITY_LIST: EventListItem[] = [
  {
    level: '0',
    keyword: 'kern',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_KERN');
    },
  },
  {
    level: '1',
    keyword: 'user',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_USER');
    },
  },
  {
    level: '2',
    keyword: 'mail',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_MAIL');
    },
  },
  {
    level: '3',
    keyword: 'daemon',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_DAEMON');
    },
  },
  {
    level: '4',
    keyword: 'auth',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_AUTH');
    },
  },
  {
    level: '5',
    keyword: 'syslog',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_SYSLOG');
    },
  },
  {
    level: '6',
    keyword: 'lpr',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_LPR');
    },
  },
  {
    level: '7',
    keyword: 'news',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_NEWS');
    },
  },
  {
    level: '8',
    keyword: 'uucp',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_UUCP');
    },
  },
  {
    level: '9',
    keyword: 'cron',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_CRON');
    },
  },
  {
    level: '10',
    keyword: 'authpriv',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_AUTH');
    },
  },
  {
    level: '11',
    keyword: 'ftp',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_FTP');
    },
  },
  {
    level: '12',
    keyword: 'ntp',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_NTP');
    },
  },
  {
    level: '13',
    keyword: 'security',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_SECURITY');
    },
  },
  {
    level: '14',
    keyword: 'console',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_CONSOLE');
    },
  },
  {
    level: '15',
    keyword: 'solaris-cron',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_SOLARIS_CRON');
    },
  },
  ...Array.from({ length: 8 }, (_, i) => ({
    level: String(16 + i),
    keyword: `local${i}`,
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_FACILITY_DESC_LOCAL');
    },
  })),
];

export const SYSLOG_SEVERITY_LIST: EventListItem[] = [
  {
    level: '0',
    keyword: 'emergency',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_EMERGENCY');
    },
  },
  {
    level: '1',
    keyword: 'alert',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_ALERT');
    },
  },
  {
    level: '2',
    keyword: 'critical',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_CRITICAL');
    },
  },
  {
    level: '3',
    keyword: 'error',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_ERROR');
    },
  },
  {
    level: '4',
    keyword: 'warning',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_WARNING');
    },
  },
  {
    level: '5',
    keyword: 'notification',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_NOTIFICATION');
    },
  },
  {
    level: '6',
    keyword: 'information',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_INFORMATION');
    },
  },
  {
    level: '7',
    keyword: 'debugging',
    get description() {
      return i18n.global.t('MESSAGE.SYSLOG_SEVERITY_DESC_DEBUGGING');
    },
  },
];

export const GENERIC_TRAP_TYPE_LIST: EventListItem[] = [
  {
    level: '0',
    keyword: 'coldStart',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_COLD_START');
    },
  },
  {
    level: '1',
    keyword: 'warmStart',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_WARM_START');
    },
  },
  {
    level: '2',
    keyword: 'linkDown',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_LINK_DOWN');
    },
  },
  {
    level: '3',
    keyword: 'linkUp',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_LINK_UP');
    },
  },
  {
    level: '4',
    keyword: 'authenticationFailure',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_AUTHENTICATION_FAILURE');
    },
  },
  {
    level: '5',
    keyword: 'egpNeighborLoss',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_EGP_NEIGHBOR_LOSS');
    },
  },
  {
    level: '6',
    keyword: 'enterpriseSpecific',
    get description() {
      return i18n.global.t('MESSAGE.GENERIC_TRAP_DESC_ENTERPRISE_SPECIFIC');
    },
  },
];

// NOTE: 추가 지원 예정
export type K8S_RESOURCE_STATUS_TYPE = 'Phase' /* | 'Condition' */ | 'Event';
const RESOURCE_STATUS_TYPE_LIST: SelectOption<K8S_RESOURCE_STATUS_TYPE>[] = [
  { name: 'Phase', value: 'Phase' },
  // { name: 'Condition', value: 'Condition' },
  { name: 'Event', value: 'Event' },
];

const KUBE_TARGET_KIND_ITEMS: SelectOption<string>[] = [
  { name: 'Pod', value: 'pod' },
  { name: 'Node', value: 'node' },
  { name: 'Deployment', value: 'deployment' },
  { name: 'Statefulset', value: 'statefulset' },
  { name: 'Daemonset', value: 'daemonset' },
  { name: 'Replicaset', value: 'replicaset' },
  { name: 'Job', value: 'job' },
  { name: 'Cronjob', value: 'cronjob' },
  { name: 'Service', value: 'service' },
  { name: 'Persistent Volume Claim', value: 'persistent_volume_claim' },
  { name: 'HPA', value: 'hpa' },
];

type KUBE_KIND_ITEM = (typeof KUBE_TARGET_KIND_ITEMS)[number]['value'];

export const KUBE_KIND_ITEM_BY_STATUS_TYPE: Record<K8S_RESOURCE_STATUS_TYPE, KUBE_KIND_ITEM[]> = {
  Phase: ['pod', 'pv', 'persistent_volume_claim'],
  // Condition: [],
  Event: [
    'pod',
    'pv',
    'persistent_volume_claim',
    'node',
    'deployment',
    'statefulset',
    'daemonset',
    'replicaset',
    'job',
    'cronjob',
    'service',
    'hpa',
  ],
};

const getSelectOptionsFromEventList = (list: EventListItem[]): SelectOption<string>[] => {
  return list.map((item) => ({ name: `${item.keyword} : ${item.level}`, value: `${item.level}` }));
};
export const EVENT_VARIABLE_CONDITION_ITEMS = {
  facility: getSelectOptionsFromEventList(SYSLOG_FACILITY_LIST),
  severity: getSelectOptionsFromEventList(SYSLOG_SEVERITY_LIST),
  genericTrapType: getSelectOptionsFromEventList(GENERIC_TRAP_TYPE_LIST),
  resourceStatusType: RESOURCE_STATUS_TYPE_LIST,
  k8sKind: KUBE_TARGET_KIND_ITEMS,
  dbKind: Object.values(DB_TYPE).map((kind) => ({ name: startCase(kind), value: kind })),
};

export const ALERT_NOTIFICATION_TAB_PANELS: TabPanel<string>[] = [
  {
    text: 'Email',
    value: 'email',
    iconClass: 'icon-color-message',
  },
  {
    text: 'Slack',
    value: 'slack',
    iconClass: 'icon-color-slack',
  },
  {
    text: 'Telegram',
    value: 'telegram',
    iconClass: 'icon-color-telegram',
  },
  {
    text: 'Messaging',
    value: 'sms',
    iconClass: 'icon-color-sms',
  },
  {
    text: 'Webhook',
    value: 'webhook',
    iconClass: 'icon-color-webhook',
  },
  {
    text: 'On Site',
    value: 'onSite',
    iconClass: 'icon-color-on-site',
  },
  {
    text: 'KakaoTalk',
    value: 'kakaoTalk',
    iconClass: 'icon-kakaotalk',
  },
];

export const NOTI_SENT_INFO = {
  true: 'O',
  false: 'X',
};
export const DEFAULT_TRANSPORT_SERVER_TYPE = {
  email: 'SMTP',
  sms: 'SMS_DB',
  kakaoTalk: 'NCP',
};

export const FILE_CHANGE_MAP = {
  fileSize: 'Filesize',
  fileChange: 'Filechange',
  fileNoChange: 'Filenochange',
};
export const FILE_CHANGE_REVERSE_MAP = Object.entries(FILE_CHANGE_MAP).reduce((acc, [k, v]) => {
  acc[v] = k;
  return acc;
}, {});

export const EXTERNAL_ALERT_TYPE_LIST = ['prometheus', 'elasticsearch'];

export const TARGET_KIND_LOCALE_MAP: Record<string, string> = {
  host: 'WORD.HOST',
  gpu: 'WORD.GPU',
  node: 'WORD.KUBERNETES',
  cluster: 'WORD.KUBERNETES',
  application: 'WORD.APPLICATION',
  database: 'WORD.DATABASE',
  networkDevice: 'WORD.NETWORK_DEVICE',
  cloud: 'WORD.CLOUD',
};

export const normalizeTargetKindForDetail = (targetKind: string): string => {
  const KIND_MAP: Record<string, string> = { networkDevice: 'network_device' };
  return KIND_MAP[targetKind] ?? targetKind;
};

/**
 * 시스템 알림 상태 조건(conditionType) 컬럼 캡션 중 `startCase` 결과가
 * 환경설정 > 시스템 알림 그리드 컬럼 캡션(`systemAlert.define.ts`)과 다른 예외만 등록한다.
 * (예: `upDown` → startCase 는 'Up Down' 이지만 그리드 캡션은 'Up/Down')
 * 맵에 없는 conditionType 은 호출부에서 `startCase` 로 폴백한다 —
 * `agentDisconnected`, `icmp`, `notReady` 등은 startCase 결과가 그리드 캡션과 같으므로 등록하지 않는다.
 */
export const CONDITION_TYPE_CAPTION_MAP: Record<string, string> = {
  upDown: 'Up/Down',
  apiCallTimeout: 'API Timeout Call',
  dbDisconnected: 'DB Disconnected',
  webserverDisconnected: 'Web Server Disconnected',
  aiAnomalyDetection: 'AI Anomaly Detection',
};
